import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Calculator,
  Users,
  Clock,
  IndianRupee,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  FileText,
  Copy,
  Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// B2B Pricing Configuration
const B2B_PRICING = {
  tiers: {
    starter: {
      name: "Starter",
      minStudents: 25,
      maxStudents: 100,
      listPrice: 999,
      floorPrice: 699,
      dailyLimitHours: 1,
      teachersIncluded: 5,
      teacherAddonPrice: 499,
      costPerStudent: 264,
    },
    plus: {
      name: "Plus",
      minStudents: 101,
      maxStudents: 300,
      listPrice: 1499,
      floorPrice: 1099,
      dailyLimitHours: 2,
      teachersIncluded: 15,
      teacherAddonPrice: 399,
      costPerStudent: 353,
    },
    pro: {
      name: "Pro",
      minStudents: 301,
      maxStudents: 500,
      listPrice: 1999,
      floorPrice: 1499,
      dailyLimitHours: 4,
      teachersIncluded: 25,
      teacherAddonPrice: 299,
      costPerStudent: 473,
    },
    enterprise: {
      name: "Enterprise",
      minStudents: 501,
      maxStudents: 99999,
      listPrice: 1799,
      floorPrice: 1299,
      dailyLimitHours: 4,
      teachersIncluded: 50,
      teacherAddonPrice: 199,
      costPerStudent: 450,
    },
  },
  gstPercent: 18,
  durationDiscounts: {
    3: 0,
    6: 10,
  },
};

const getTierForStudents = (numStudents) => {
  if (numStudents <= 100) return "starter";
  if (numStudents <= 300) return "plus";
  if (numStudents <= 500) return "pro";
  return "enterprise";
};

export default function SalesPricingCalculator() {
  const [schoolName, setSchoolName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [students, setStudents] = useState(100);
  const [duration, setDuration] = useState(6);
  const [additionalTeachers, setAdditionalTeachers] = useState(0);
  const [perStudentPrice, setPerStudentPrice] = useState(999);
  const [calculation, setCalculation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const tier = getTierForStudents(students);
  const tierConfig = B2B_PRICING.tiers[tier];

  // Update price when tier changes
  useEffect(() => {
    const newTier = getTierForStudents(students);
    const newTierConfig = B2B_PRICING.tiers[newTier];
    // Set to list price when tier changes
    if (perStudentPrice < newTierConfig.floorPrice || perStudentPrice > newTierConfig.listPrice) {
      setPerStudentPrice(newTierConfig.listPrice);
    }
  }, [students]);

  // Calculate pricing
  const calculatePricing = useCallback(() => {
    const tierConfig = B2B_PRICING.tiers[tier];
    
    // Base calculation
    const baseAmount = students * perStudentPrice * duration;
    
    // Duration discount
    const discountPercent = B2B_PRICING.durationDiscounts[duration] || 0;
    const discountAmount = baseAmount * (discountPercent / 100);
    const discountedAmount = baseAmount - discountAmount;
    
    // Additional teachers
    const quarters = Math.ceil(duration / 3);
    const teacherAmount = additionalTeachers * tierConfig.teacherAddonPrice * quarters;
    
    // Subtotal before GST
    const subtotal = discountedAmount + teacherAmount;
    
    // GST
    const gstAmount = subtotal * (B2B_PRICING.gstPercent / 100);
    
    // Total
    const totalAmount = subtotal + gstAmount;
    
    // Cost and profit
    const totalCost = students * tierConfig.costPerStudent * duration;
    const profit = subtotal - totalCost;
    const marginPercent = subtotal > 0 ? (profit / subtotal) * 100 : 0;

    setCalculation({
      tier,
      tierName: tierConfig.name,
      students,
      perStudentPrice,
      duration,
      baseAmount,
      discountPercent,
      discountAmount,
      discountedAmount,
      additionalTeachers,
      teachersIncluded: tierConfig.teachersIncluded,
      teacherAddonPrice: tierConfig.teacherAddonPrice,
      teacherAmount,
      subtotal,
      gstPercent: B2B_PRICING.gstPercent,
      gstAmount,
      totalAmount,
      cost: totalCost,
      profit,
      marginPercent,
      listPrice: tierConfig.listPrice,
      floorPrice: tierConfig.floorPrice,
      dailyLimitHours: tierConfig.dailyLimitHours,
    });
  }, [tier, students, perStudentPrice, duration, additionalTeachers]);

  useEffect(() => {
    calculatePricing();
  }, [calculatePricing]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMarginStatus = (margin) => {
    if (margin >= 70) return { color: "text-green-600", bg: "bg-green-100", label: "Excellent" };
    if (margin >= 50) return { color: "text-green-600", bg: "bg-green-100", label: "Healthy" };
    if (margin >= 30) return { color: "text-yellow-600", bg: "bg-yellow-100", label: "Acceptable" };
    return { color: "text-red-600", bg: "bg-red-100", label: "Low" };
  };

  const handleSaveQuote = async () => {
    if (!schoolName.trim()) {
      toast.error("Please enter school name");
      return;
    }
    if (!contactEmail.trim()) {
      toast.error("Please enter contact email");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/quotes/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          school_name: schoolName,
          contact_email: contactEmail,
          students,
          per_student_price: perStudentPrice,
          duration_months: duration,
          additional_teachers: additionalTeachers,
        }),
      });

      const data = await res.json();
      if (data.success) {
        toast.success(`Quote ${data.quote.id} created successfully!`);
      } else {
        toast.error(data.detail || "Failed to create quote");
      }
    } catch (error) {
      console.error("Failed to save quote:", error);
      toast.error("Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = () => {
    if (!calculation) return;
    
    const text = `
EDUZY B2B QUOTE
===============
School: ${schoolName || "TBD"}
Tier: ${calculation.tierName}
Students: ${calculation.students}
Duration: ${calculation.duration} months

Per Student/Month: ${formatCurrency(calculation.perStudentPrice)}
Base Amount: ${formatCurrency(calculation.baseAmount)}
${calculation.discountPercent > 0 ? `Discount (${calculation.discountPercent}%): -${formatCurrency(calculation.discountAmount)}` : ""}
${calculation.additionalTeachers > 0 ? `Additional Teachers (${calculation.additionalTeachers}): ${formatCurrency(calculation.teacherAmount)}` : ""}

Subtotal: ${formatCurrency(calculation.subtotal)}
GST (18%): ${formatCurrency(calculation.gstAmount)}
─────────────────
TOTAL: ${formatCurrency(calculation.totalAmount)}

* 18% GST Extra
* All subjects included
* ${calculation.teachersIncluded} teacher accounts included
* ${calculation.dailyLimitHours} hours/day learning limit
    `.trim();

    navigator.clipboard.writeText(text);
    toast.success("Quote copied to clipboard!");
  };

  const handleDownloadPDF = async () => {
    if (!schoolName.trim() || !contactEmail.trim()) {
      toast.error("Please enter school name and contact email");
      return;
    }

    setDownloadingPDF(true);
    try {
      const res = await fetch(`${API}/api/sales/quotes/preview-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          school_name: schoolName,
          contact_email: contactEmail,
          students,
          per_student_price: perStudentPrice,
          duration_months: duration,
          additional_teachers: additionalTeachers,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to generate PDF");
      }

      // Get the PDF blob
      const blob = await res.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Eduzy_Quote_${schoolName.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPDF(false);
    }
  };

  const marginStatus = calculation ? getMarginStatus(calculation.marginPercent) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">B2B Pricing Calculator</h1>
          <p className="text-gray-500">Calculate school pricing with cost and margin analysis</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* School Details */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">School Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="schoolName">School Name</Label>
                  <Input
                    id="schoolName"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    placeholder="e.g., Delhi Public School"
                    className="bg-gray-50"
                    data-testid="school-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="principal@school.edu.in"
                    className="bg-gray-50"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Configuration */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">Pricing Configuration</CardTitle>
              <CardDescription>
                Detected Tier: <Badge className="ml-2 bg-blue-100 text-blue-700">{tierConfig.name}</Badge>
                <span className="ml-2 text-gray-500">({tierConfig.minStudents}-{tierConfig.maxStudents} students)</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Students */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    Number of Students
                  </Label>
                  <Input
                    type="number"
                    value={students}
                    onChange={(e) => setStudents(Math.max(25, parseInt(e.target.value) || 25))}
                    className="w-24 text-center bg-gray-50"
                    min={25}
                    data-testid="students-input"
                  />
                </div>
                <Slider
                  value={[students]}
                  onValueChange={([val]) => setStudents(val)}
                  min={25}
                  max={1000}
                  step={25}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>25</span>
                  <span>250</span>
                  <span>500</span>
                  <span>750</span>
                  <span>1000</span>
                </div>
              </div>

              {/* Per Student Price */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-gray-500" />
                    Per Student / Month
                  </Label>
                  <Input
                    type="number"
                    value={perStudentPrice}
                    onChange={(e) => setPerStudentPrice(Math.max(tierConfig.floorPrice, parseInt(e.target.value) || tierConfig.floorPrice))}
                    className="w-24 text-center bg-gray-50"
                    data-testid="price-input"
                  />
                </div>
                <Slider
                  value={[perStudentPrice]}
                  onValueChange={([val]) => setPerStudentPrice(val)}
                  min={tierConfig.floorPrice}
                  max={tierConfig.listPrice + 200}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs">
                  <span className="text-red-500">Floor: ₹{tierConfig.floorPrice}</span>
                  <span className="text-gray-500">Target</span>
                  <span className="text-green-500">List: ₹{tierConfig.listPrice}</span>
                </div>
              </div>

              {/* Duration */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  Duration
                </Label>
                <div className="flex gap-3">
                  {[3, 6].map((months) => (
                    <button
                      key={months}
                      onClick={() => setDuration(months)}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                        duration === months
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`duration-${months}-btn`}
                    >
                      <span className="font-semibold">{months} Months</span>
                      {B2B_PRICING.durationDiscounts[months] > 0 && (
                        <Badge className="ml-2 bg-green-100 text-green-700">
                          {B2B_PRICING.durationDiscounts[months]}% off
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Teachers */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Additional Teachers (beyond {tierConfig.teachersIncluded} included)</Label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAdditionalTeachers(Math.max(0, additionalTeachers - 1))}
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                    >
                      -
                    </button>
                    <Input
                      type="number"
                      value={additionalTeachers}
                      onChange={(e) => setAdditionalTeachers(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-16 text-center bg-gray-50"
                      min={0}
                      data-testid="additional-teachers-input"
                    />
                    <button
                      onClick={() => setAdditionalTeachers(additionalTeachers + 1)}
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                    >
                      +
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  ₹{tierConfig.teacherAddonPrice}/teacher/quarter
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Quote Summary */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-xl">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Quote Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {calculation && (
                <>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Students</span>
                      <span className="font-medium">{calculation.students}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Per Student/Month</span>
                      <span className="font-medium">{formatCurrency(calculation.perStudentPrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Duration</span>
                      <span className="font-medium">{calculation.duration} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Base Amount</span>
                      <span className="font-medium">{formatCurrency(calculation.baseAmount)}</span>
                    </div>
                    {calculation.discountPercent > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({calculation.discountPercent}%)</span>
                        <span>-{formatCurrency(calculation.discountAmount)}</span>
                      </div>
                    )}
                    {calculation.additionalTeachers > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Teachers (+{calculation.additionalTeachers})</span>
                        <span className="font-medium">{formatCurrency(calculation.teacherAmount)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-3 flex justify-between">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="font-semibold">{formatCurrency(calculation.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>GST ({calculation.gstPercent}%)</span>
                      <span>{formatCurrency(calculation.gstAmount)}</span>
                    </div>
                    <div className="border-t-2 border-gray-300 pt-3 flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total</span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(calculation.totalAmount)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                    <p className="text-orange-700 text-sm font-medium">
                      * 18% GST Extra
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={copyToClipboard}
                      data-testid="copy-quote-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                    <Button
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
                      onClick={handleSaveQuote}
                      disabled={saving}
                      data-testid="save-quote-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {saving ? "Saving..." : "Save Quote"}
                    </Button>
                  </div>
                  
                  {/* Download PDF Button */}
                  <Button
                    variant="outline"
                    className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={handleDownloadPDF}
                    disabled={downloadingPDF || !schoolName.trim() || !contactEmail.trim()}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingPDF ? "Generating PDF..." : "Download Quote PDF"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Profitability Analysis (Internal) */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Profitability (Internal)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {calculation && marginStatus && (
                <>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Your Cost</span>
                      <span className="font-medium text-red-600">{formatCurrency(calculation.cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Revenue (Subtotal)</span>
                      <span className="font-medium">{formatCurrency(calculation.subtotal)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3 flex justify-between">
                      <span className="text-gray-500">Gross Profit</span>
                      <span className="font-semibold text-green-600">{formatCurrency(calculation.profit)}</span>
                    </div>
                  </div>

                  <div className={`rounded-xl p-4 ${marginStatus.bg}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${marginStatus.color}`}>Margin</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-2xl font-bold ${marginStatus.color}`}>
                          {calculation.marginPercent.toFixed(1)}%
                        </span>
                        {calculation.marginPercent >= 50 ? (
                          <CheckCircle2 className={`w-5 h-5 ${marginStatus.color}`} />
                        ) : (
                          <AlertCircle className={`w-5 h-5 ${marginStatus.color}`} />
                        )}
                      </div>
                    </div>
                    <p className={`text-sm mt-1 ${marginStatus.color}`}>{marginStatus.label}</p>
                  </div>

                  <div className="text-xs text-gray-500 space-y-1">
                    <p>• Cost/Student/Month: ₹{tierConfig.costPerStudent}</p>
                    <p>• Teachers Included: {calculation.teachersIncluded}</p>
                    <p>• Daily Limit: {calculation.dailyLimitHours} hours</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Pricing Guide */}
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-gray-900 mb-3">Pricing Guide</h4>
              <div className="space-y-2 text-sm">
                {Object.entries(B2B_PRICING.tiers).map(([key, config]) => (
                  <div 
                    key={key}
                    className={`flex justify-between p-2 rounded ${tier === key ? 'bg-blue-100' : ''}`}
                  >
                    <span className={tier === key ? 'font-medium text-blue-700' : 'text-gray-600'}>
                      {config.name} ({config.minStudents}-{config.maxStudents === 99999 ? '∞' : config.maxStudents})
                    </span>
                    <span className={tier === key ? 'font-medium text-blue-700' : 'text-gray-600'}>
                      ₹{config.floorPrice} - ₹{config.listPrice}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
